import React from 'react';
import { ArrowPathIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import Button from 'lib/components/button.jsx';
import { captureFormSubmit, captureFormChange } from 'lib/utils/form.js';
import { isFunction } from 'lib/utils/function.js';
import { mergeClasses } from 'lib/helpers/class.js';
import { Icon } from '@iconify-icon/react';

export function useSearchForm(searchParams, onSubmit, onChange) {
    const [isNewSearch, setIsNewSearch] = React.useState(true);
    const isRefreshing = !isNewSearch;

    const _onChange = React.useCallback(
        (event) => {
            const data = captureFormChange(event);
            if (searchParams) {
                const entries = Object.entries(data);
                const isNewSearch = entries.some(([key, value]) => searchParams[key] !== value);
                setIsNewSearch(isNewSearch);
            }
            if (isFunction(onChange)) onChange(data, event);
        },
        [searchParams, onChange]
    );

    const _onSubmit = React.useCallback(
        (event) => {
            const data = captureFormSubmit(event);
            if (searchParams) setIsNewSearch(false);
            if (isFunction(onSubmit)) onSubmit(data, event);
        },
        [searchParams, onSubmit]
    );

    return {
        isRefreshing,
        isNewSearch,
        onChange: _onChange,
        onSubmit: _onSubmit
    };
}

export default function SearchPanel(props) {
    const {
        searchParams,
        onSubmit: providedOnSubmit,
        onChange: providedOnChange,
        onExport,
        onExportLoad,
        children,
        className,
        busy,
        ...rest
    } = props;

    const { onChange, onSubmit, isRefreshing, isNewSearch } = useSearchForm(
        searchParams,
        providedOnSubmit,
        providedOnChange
    );

    return (
        <form
            className={mergeClasses(
                'mb-6 flex items-end gap-3 border-b bg-white p-2 shadow-md md:gap-4 md:rounded-lg md:border md:p-5 md:pt-4 lg:p-6 lg:pt-5',
                className
            )}
            onSubmit={onSubmit}
            onChange={onChange}
            {...rest}
        >
            {children}
            <Button look="primary" type="submit" busy={busy}>
                {isRefreshing && (
                    <React.Fragment>
                        <ArrowPathIcon className="icon -my-0.5" />
                        <span className="sr-only">{_t.global.refresh()}</span>
                    </React.Fragment>
                )}
                {isNewSearch && (
                    <React.Fragment>
                        <Icon icon="material-symbols:search-rounded" width="1.2rem" height="1.2rem" />
                        <span className="sr-only">{_t.global.search()}</span>
                    </React.Fragment>
                )}
            </Button>

            {onExport && (
                <Button look="primary" type="button" onClick={onExport} busy={onExportLoad}>
                    <>
                        <ArrowDownTrayIcon className="icon -my-0.5" />
                        <span className="sr-only">{_t.global.export()}</span>
                    </>
                </Button>
            )}
        </form>
    );
}
